/**
 * React Query hooks for the in-app notification feed (P133, #480).
 *
 * Mirrors backend/shared/notifications_router.py.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { notificationKeys } from "shared/api/queryClient";
import type { AppNotification } from "shared/api/types";

export function useNotifications(): UseQueryResult<AppNotification[], ApiError> {
  return useQuery({
    queryKey: notificationKeys.feed(),
    queryFn: () => api.get<AppNotification[]>("/v1/me/notifications"),
  });
}

export function useUnreadCount(): UseQueryResult<{ count: number }, ApiError> {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => api.get<{ count: number }>("/v1/me/notifications/unread-count"),
    // Poll for the bell badge -- 60s is the MVP cadence (no live push).
    refetchInterval: 60_000,
  });
}

export function useMarkRead(): UseMutationResult<unknown, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/v1/me/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useAcknowledge(): UseMutationResult<unknown, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/v1/me/notifications/${id}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllRead(): UseMutationResult<unknown, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/v1/me/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
